/**
 * `createApiPipelinesSource` — the `pipeline-mgmt-api` port over the shared
 * `ApiClient`.
 *
 * The visual builder (`@authe/pipeline-builder`) edits a camelCase
 * {@link PipelineDoc}; `pipeline-mgmt-api` speaks snake_case (`step_name`,
 * `target_tier`, node `ui` hints, …). This adapter crosses that boundary with
 * the dependency-free wire mappers from `@authe/fe-commons/pipeline`
 * (`docToWire` / `docFromWire` / `catalogFromWire` / `issuesFromWire` /
 * `runResultFromWire`), so the wire stays canonically snake_case and the
 * builder stays untouched. Canonical paths (`/pipelines/*`) are mapped to the
 * `private/pipeline-mgmt-api` tier by `RESOURCE_REWRITES`.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import {
  catalogFromWire,
  docFromWire,
  docToWire,
  issuesFromWire,
  runResultFromWire,
  templatesFromWire,
  type PipelineDoc,
  type PipelineIssue,
  type PipelineRunResult,
  type PipelineTemplate,
  type StepCatalog,
} from "@authe/fe-commons/pipeline";

export type PipelineScopeWire = "system" | "customer" | "org" | "tenant" | "user" | "team";

export interface PipelineDefinitionSummary {
  id: string;
  name: string;
  description?: string | null;
  scope: string;
  enabled: boolean;
  isDefault: boolean;
  doc: PipelineDoc;
}

export interface SavePipelineOptions {
  scope?: PipelineScopeWire;
  description?: string | null;
  enabled?: boolean;
  isDefault?: boolean;
  targetChainIds?: string[];
}

export interface PipelinesSource {
  /** Step palette from the live backend registry. */
  catalog(): Promise<StepCatalog>;
  /**
   * Template gallery from the backend SSOT
   * (`authe_tx_pipeline.build_template_catalog`, served at
   * `GET /v0/pipelines/templates`). The builder falls back to its bundled
   * `DEFAULT_TEMPLATES` when this yields nothing.
   */
  templates(): Promise<PipelineTemplate[]>;
  /** Pipeline definitions visible to the caller (system defaults + tenant rows). */
  listDefinitions(): Promise<PipelineDefinitionSummary[]>;
  /** One definition by id (deep-link target; includes its full doc). */
  get(id: string): Promise<PipelineDefinitionSummary | null>;
  /** The system/tenant default pipeline doc, if one is flagged. */
  defaultDoc(): Promise<PipelineDoc | null>;
  /** Persist a doc as a NEW definition (POST); returns the created row id. */
  save(doc: PipelineDoc, opts?: SavePipelineOptions): Promise<{ id: string }>;
  /** Update an EXISTING definition in place (PATCH); returns its id. */
  update(id: string, doc: PipelineDoc, opts?: SavePipelineOptions): Promise<{ id: string }>;
  /** Delete a definition. */
  remove(id: string): Promise<void>;
  /** Authoritative server-side validation against the registry. */
  validate(doc: PipelineDoc): Promise<PipelineIssue[]>;
  /** Run the doc against a sample input without persisting. */
  dryRun(doc: PipelineDoc, input: unknown, options?: DryRunOptions): Promise<PipelineRunResult>;
}

/** Run-time knobs for a dry run (not part of the doc). */
export interface DryRunOptions {
  /** Chain the sample tx is on — the normalize step requires it. */
  chainId?: string;
  /** Ingress provider the sample payload is shaped for (default `moralis`). */
  provider?: string;
}

function definitionFromWire(raw: Record<string, any>): PipelineDefinitionSummary {
  return {
    id: raw.id,
    name: raw.name ?? "",
    description: raw.description ?? null,
    scope: String(raw.scope ?? "user"),
    enabled: Boolean(raw.enabled ?? true),
    isDefault: Boolean(raw.is_default ?? raw.isDefault ?? false),
    doc: docFromWire((raw.doc as Record<string, any>) ?? {}),
  };
}

export function createApiPipelinesSource(client: ApiClient): PipelinesSource {
  return {
    async catalog(): Promise<StepCatalog> {
      const res = await client.get<Record<string, any>>("/pipelines/catalog");
      return catalogFromWire(unwrapData(res) ?? {});
    },

    async templates(): Promise<PipelineTemplate[]> {
      const res = await client.get<Record<string, any>>("/pipelines/templates");
      return templatesFromWire(unwrapData(res) ?? {});
    },

    async listDefinitions(): Promise<PipelineDefinitionSummary[]> {
      const res = await client.get<Record<string, any>>("/pipelines");
      const data = unwrapData(res);
      const items = Array.isArray(data) ? data : (data?.items ?? []);
      return items.map(definitionFromWire);
    },

    async get(id: string): Promise<PipelineDefinitionSummary | null> {
      const res = await client.get<Record<string, any>>(`/pipelines/${encodeURIComponent(id)}`);
      const data = unwrapData(res);
      return data ? definitionFromWire(data) : null;
    },

    async defaultDoc(): Promise<PipelineDoc | null> {
      const definitions = await this.listDefinitions();
      const preferred = definitions.find((d) => d.isDefault) ?? definitions[0];
      return preferred ? preferred.doc : null;
    },

    async save(doc: PipelineDoc, opts: SavePipelineOptions = {}): Promise<{ id: string }> {
      const body = {
        name: doc.name,
        description: opts.description ?? doc.description ?? null,
        scope: opts.scope ?? "user",
        enabled: opts.enabled ?? true,
        is_default: opts.isDefault ?? false,
        target_chain_ids: opts.targetChainIds ?? [],
        doc: docToWire(doc),
      };
      const created = unwrapData(await client.post<Record<string, any>>("/pipelines", body));
      // Drop the 30s GET cache so the list reflects the new definition.
      client.invalidate("/pipelines");
      return { id: created?.id ?? doc.id };
    },

    async update(id: string, doc: PipelineDoc, opts: SavePipelineOptions = {}): Promise<{ id: string }> {
      // PATCH only the keys we send; `scope` is immutable on update server-side.
      const body: Record<string, unknown> = { name: doc.name, doc: docToWire(doc) };
      if (opts.description !== undefined) body.description = opts.description;
      else if (doc.description !== undefined) body.description = doc.description ?? null;
      if (opts.enabled !== undefined) body.enabled = opts.enabled;
      if (opts.isDefault !== undefined) body.is_default = opts.isDefault;
      if (opts.targetChainIds !== undefined) body.target_chain_ids = opts.targetChainIds;
      const updated = unwrapData(
        await client.patch<Record<string, any>>(`/pipelines/${encodeURIComponent(id)}`, body),
      );
      client.invalidate("/pipelines");
      client.invalidate(`/pipelines/${id}`);
      return { id: updated?.id ?? id };
    },

    async remove(id: string): Promise<void> {
      await client.delete<void>(`/pipelines/${encodeURIComponent(id)}`);
      client.invalidate("/pipelines");
      client.invalidate(`/pipelines/${id}`);
    },

    async validate(doc: PipelineDoc): Promise<PipelineIssue[]> {
      const res = await client.post<Record<string, any>>("/pipelines/validate", { doc: docToWire(doc) });
      return issuesFromWire(unwrapData(res) ?? []);
    },

    async dryRun(doc: PipelineDoc, input: unknown, options: DryRunOptions = {}): Promise<PipelineRunResult> {
      const body: Record<string, unknown> = {
        doc: docToWire(doc),
        input: input ?? {},
        target_tier: doc.targetTier,
      };
      // Prefer the explicit dry-run chain, falling back to the doc's config scope.
      const chainId = options.chainId || doc.scope?.chainId || undefined;
      if (chainId) body.chain_id = chainId;
      if (options.provider) body.provider = options.provider;
      const res = await client.post<Record<string, any>>("/pipelines/dry-run", body);
      return runResultFromWire(unwrapData(res) ?? {});
    },
  };
}
